<template>
    <modal-controller-base contentClass="light-light-background" footer>
        <template #header>
            <custom-header class="barcode-scanner-visible" back-button :title="$t('titleRoute.scanner')">
                <template #start>
                    <ion-button
                        @click="modalController.dismiss()"
                        class="purple-light-color"
                    >
                        <ion-icon :icon="chevronBack"/>
                        {{ $t('button.back') }}
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <div v-if="!currentScanList.length" class="max-height d-flex center flex-d-c">
            <ion-img src="/assets/icon/barcode-scan.svg" class="mb-3"/>
            <ion-text class="fw-b fs-18 text-center purple-purple-color " style="width: 260px;">
                {{ $t('messages.scanDesiredProducts') }}
            </ion-text>
            <ion-row>
                <ion-col class="center">
                    <button-base v-if="!listStore.isScanHardware" @click="startScan()">{{
                            $t('button.openCameraScanner')
                        }}
                    </button-base>
                </ion-col>
            </ion-row>
        </div>

        <div v-else>
            <ion-row v-if="showButton && !listStore.isScanHardware" style="border-bottom: 2px solid var(--ion-color-grey4);">
                <ion-col class="center">
                    <button-base @click="startScan()">{{
                            $t('button.openCameraScanner')
                        }}
                    </button-base>
                </ion-col>
            </ion-row>

            <div ref="scanItemsEl">
                <!--This function does exist https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/toReversed-->
                <template v-for="catalogItem of [...currentScanList].reverse()" :key="catalogItem.id">
                    <scan-details-sliding
                        style="width: 100%;"
                        :edit="userStore.permission[PERMISSION_KEY.CART_UPDATE] || userStore.permission[PERMISSION_KEY.CART_DELETE]"
                        :entry="catalogItem" :listId="supplierId"
                        @amount="changeItemQuantity(supplierId, catalogItem.id, $event)"
                        @click="openScanModalProduct(catalogItem, true)"
                    />
                </template>
            </div>

            <ion-row class="py-3">
                <ion-col class="center">
                    <ion-img src="/assets/icon/barcode-scan.svg" style="width: 40px;" class="mr-2"/>
                    <ion-text
                        style="max-width: 150px; line-height: 20px;"
                        class="purple-purple-color fs-smaller fw-b"
                    >
                        {{ $t('messages.scanToAddMoreProducts') }}
                    </ion-text>
                </ion-col>
            </ion-row>
            <ion-row>
                <ion-col class="center">
                    <button-base v-if="!listStore.isScanHardware" @click="startScan()">{{
                            $t('button.openCameraScanner')
                        }}
                    </button-base>
                </ion-col>
            </ion-row>
        </div>

        <template #footer>
            <ion-grid class="light-light-background purple-light-color barcode-scanner-visible">
                <div class="center">
                    <div class="center py-1" style="position: relative;">
                        <div class="center-y" style="position: absolute; top: 10px; right: 80px;">
                            <ion-text class="fs-smaller pr-2 purple-purple-color">
                                {{ $t('label.scanner') }}
                            </ion-text>
                            <ion-img class="scanner-icon" src="/assets/icon/barcode.svg"/>
                        </div>

                        <ion-toggle
                            @ionChange="handleScanToggle" v-model="listStore.isScanHardware"
                            class="accordion-toggle px-2" mode="ios"
                            :color="ION_VARIANT.CORAL"
                        />

                        <div class="center-y" style="position: absolute; top: 8px; left: 80px;">
                            <ion-img class="camera-icon" src="/assets/icon/camera-outline.svg"/>
                            <ion-text class="fs-smaller pl-1 purple-purple-color">
                                {{ $t('label.camera') }}
                            </ion-text>
                        </div>
                    </div>
                </div>
            </ion-grid>

            <ion-grid
                class="purple-light-color p-0  barcode-scanner-visible" :class="{
                'light-purple-background': isDark() && currentScanList.length,
                'grey4-grey4-background': !isDark() && currentScanList.length,
                'light-light-background': !currentScanList.length
            }" :style="(isPlatform('ios')) ? 'padding-bottom: 34px' : ''"
            >
                <ion-row>
                    <ion-col size="5" class="center-y pl-2" style="min-height: 30px">
                        <div>
                            <div class="fs-smaller mt-1 grey1-grey3-color">
                                {{ $t('messages.cart.amountItems', { amount: amount.items }) }}
                            </div>
                            <div class="fs-smaller mt-1 grey1-grey3-color">
                                {{ $t('messages.cart.amount', { amount: amount.positions }) }}
                            </div>
                        </div>
                    </ion-col>

                    <ion-col
                        v-if="!persistentStore.offlineMode" offset="1" size="6"
                        class="ion-justify-content-end d-flex ion-align-items-center pr-2"
                    >
                        <button-base
                            :disabled="isDisableAddCart || currentScanList.length < 1"
                            class="wrap-button fw-b m-0"
                            @click="checkOverrideScanList"
                        >
                            {{ $t('button.updateCart') }}
                        </button-base>
                    </ion-col>
                </ion-row>
            </ion-grid>
        </template>
    </modal-controller-base>
</template>

<script setup lang="ts">
import { IonButton, IonCol, IonGrid, IonIcon, IonImg, IonRow, IonText, IonToggle, modalController } from "@ionic/vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";
import { alertController, isPlatform } from "@ionic/core";

import ButtonBase from "@/components/button/ButtonBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import ScanProduct from "@/components/modal/ScanProduct.vue";
import ScanDetailsSliding from "@/components/various/ScanDetailsSliding.vue";

import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import { usePersistentStore } from "@/stores/persistent";
import { useUserStore } from "@/stores/user";

import { useLoader } from "@/composables/useLoader";

import { popAlertConfirm, popAlertOk } from "@/functions/alert";
import { isDark } from "@/functions/theme";

import { CatalogItem } from "@/types/supplier";

import { chevronBack } from "ionicons/icons";
import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { PERMISSION_KEY } from "@/types/user";

// ─── NEW: @capacitor/barcode-scanner ────────────────────────────────────────────
// This plugin has a single method: CapacitorBarcodeScanner.scanBarcode(options)
// It opens a native scanner activity/view and returns a Promise with the result.
// No listeners, no background hiding, no permissions API — all handled natively.
import {
    CapacitorBarcodeScanner,
    CapacitorBarcodeScannerTypeHintALLOption,
    CapacitorBarcodeScannerCameraDirection,
    CapacitorBarcodeScannerScanOrientation,
    type CapacitorBarcodeScannerScanResult,
} from '@capacitor/barcode-scanner';
// ─────────────────────────────────────────────────────────────────────────────────

/**
 * Starts a barcode scan using the native scanner activity.
 * @capacitor/barcode-scanner opens a full-screen native scanner — no WebView
 * transparency or CSS hacks needed. The promise resolves when the user scans
 * a barcode or cancels.
 */
const startScan = async () => {
    try {
        const result: CapacitorBarcodeScannerScanResult = await CapacitorBarcodeScanner.scanBarcode({
            hint: CapacitorBarcodeScannerTypeHintALLOption.ALL,
            cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
            scanOrientation: CapacitorBarcodeScannerScanOrientation.PORTRAIT,
            scanInstructions: t('messages.scanDesiredProducts'),
            scanButton: false,
            scanText: '',
            android: {
                scanningLibrary: 'mlkit' as any, // or 'zxing'
            },
        });

        console.log("SCAN VALUE", result.ScanResult);

        // result.ScanResult is an empty string when the user cancels
        if (result.ScanResult) {
            await addSupplierCatalogItemByEan(result.ScanResult);
        }
    } catch (error) {
        console.error('Barcode scan error:', error);
    }
};

interface ScanModalProps {
    supplierId: string;
}

const props = defineProps<ScanModalProps>()

const persistentStore = usePersistentStore()
const {
    scanList,
    listItems,
    addListItem,
    addItemToCart,
    patchListItem,
    getListItems,
    clearScanList,
    addScanItem,
    changeItemQuantity,
} = useListStore()
const listStore = useListStore()
const supplierStore = useSupplierStore()
const userStore = useUserStore()
const { showLoading, hideLoading } = useLoader()

let scanResult = ''

const currentScanList = computed<Array<CatalogItem>>(() => scanList[props.supplierId] ?? [])
const amount = computed<{ items: number, positions: number }>(() => {
    return currentScanList.value.reduce((value: {
        items: number,
        positions: number,
        ids: Array<string>
    }, list: CatalogItem) => {
        if (value.ids.includes(list.id)) {
            value.items += list.quantity ?? 1
        } else {
            value.ids.push(list.id)
            value.items += list.quantity ?? 1
            value.positions += 1
        }

        return value
    }, { items: 0, positions: 0, ids: [] })
})
const locationId = computed<string>(() => String(route.value.params.locationId))
const isDisableAddCart = computed<boolean>(() => !amount.value.positions)
const allItems = computed(() => {
    return listItems[props.supplierId]?.data?.length
        ? scanList[props.supplierId].reduce((items: {
            newItems: Array<CatalogItem>,
            otherItems: Array<CatalogItem>
        }, item: CatalogItem) => {
            const listItem = listItems[props.supplierId].data.find((list) => list.supplierItemId === item.id)
            if (listItem?.id) {
                item.listItemId = listItem.id
                items.otherItems.push(item)
            } else items.newItems.push(item)

            return items
        }, { newItems: [], otherItems: [] })
        : { newItems: scanList[props.supplierId], otherItems: [] }
})

const handleScanToggle = (ev: Event) => {
    console.log("EVENT", (ev.target as HTMLIonToggleElement)?.checked)
    // true = device scanner, false = camera scanner
}

const addToList = async (): Promise<void> => {
    alertController.dismiss()
    await showLoading()

    for (const catalogItem of allItems.value.newItems) {
        await addItemToCart(locationId.value, catalogItem.id, catalogItem.quantity ?? 1)
    }

    for (const catalogItem of allItems.value.otherItems) {
        // patchCartItem(locationId: string, itemId: string, quantity: number)
    }

    listStore.getShoppingCart(locationId.value)
    hideLoading()
    modalController.dismiss()
    clearScanList(props.supplierId)
}

const checkOverrideScanList = (): void => {
    popAlertConfirm({
        message: t(`messages.${allItems.value.otherItems.length ? 'updateScanItems' : 'addScanItems'}`),
        cancelText: t('button.cancel'),
        handler: {
            ok: addToList
        }
    })
}

const openScanModalProduct = async (catalogItem: CatalogItem, isModal = false): Promise<void> => {
    const currentItem = currentScanList.value.find((item) => item.id === catalogItem.id) ?? catalogItem

    if (currentItem?.quantity !== 1 && !isModal) return

    const modal = await modalController.create({
        id: 'EAN_' + String(currentItem.id),
        component: ScanProduct,
        cssClass: 'scrollable-at-max-breakpoint height-auto',
        componentProps: {
            catalogItem: currentItem,
            supplierId: props.supplierId
        },
        breakpoints: [0, 1],
        initialBreakpoint: 1
    })

    await modal.present()
}

const addSupplierCatalogItemByEan = async (scannerResponse: string): Promise<boolean> => {

    const response = await supplierStore.getSupplierCatalogItemByEan(locationId.value, props.supplierId, scannerResponse)
    const alert = await alertController.getTop()
    if (alert) await alertController.dismiss()
    if (!response) {
        await hideLoading()
        await popAlertOk({
            message: t('error.scannedProductNotAvailable'),
            id: '404',
            handler: () => {
                // Re-open scanner after dismissing the "not found" alert
                startScan()
            }
        })
        return false
    }

    response.quantity = (response.amountStep) ? response.amountStep : 1;
    addScanItem(props.supplierId, response)

    const topModal = await modalController.getTop()
    if (topModal?.id.startsWith('EAN_')) {
        if (topModal.id !== 'EAN_' + String(response.id)) {
            await modalController.dismiss({ id: topModal.id })
            await openScanModalProduct(response, true)
        }
    } else {
        await openScanModalProduct(response, true)
    }

    await hideLoading()
    return true
}

let timeout: number | null = null
const acceptInput = () => {
    const eanCode = scanResult
    scanResult = ''
    if (eanCode) addSupplierCatalogItemByEan(eanCode)
}

const checkScannerInput = (input: KeyboardEvent) => {
    if (input.key === 'Unidentified') return
    const inputKey = input.key.toString()
    if (!listStore.isScanHardware || (isNaN(parseInt(inputKey)) && inputKey != 'Enter' && inputKey.length === 1) || (input.target as HTMLElement)?.localName === "input") return

    //  if not enter and is number add to scanResult
    if (inputKey != 'Enter' && !isNaN(parseInt(inputKey))) {
        scanResult += inputKey
    } else {
        // maybe its a longer string with not only numbers
        for (let i = 0; i < inputKey.length; i++) {
            // check every char if number -> add to scanResult
            if (!isNaN(parseInt(inputKey[i]))) scanResult += inputKey
        }
    }

    if (inputKey == 'Enter' || scanResult.length > 12) {
        if (timeout) clearTimeout(timeout)
        timeout = null
        acceptInput()
    } else if (scanResult.length === 8) {
        timeout = setTimeout(() => {
            acceptInput()
        }, 300) as unknown as number
    }
}

const listener = (input: KeyboardEvent) => checkScannerInput(input)

const backListener = (ev: Event) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    ev.detail.register(100, async (processNextHandler) => {
        processNextHandler()
    })
}

//prevent closing modal when using hardware back button
document.addEventListener('ionBackButton', backListener)

onBeforeUnmount(() => {
    document.removeEventListener('ionBackButton', backListener)
    window.removeEventListener('keyup', listener, true)
    resizeObserver?.disconnect()
    resizeObserver = null;
})

const scanItemsEl = ref<HTMLElement | null>(null)
const showButton = ref(false)
let resizeObserver: ResizeObserver | null = null

function calculateHeight () {
    const contentHeight = document.querySelector('.scanner-modal')?.querySelector('ion-content')?.scrollHeight || 0;
    showButton.value = (scanItemsEl.value?.scrollHeight || 0) + 50 > contentHeight
}

onMounted(() => {
    resizeObserver = new ResizeObserver(calculateHeight)

    if (scanItemsEl.value) {
        resizeObserver.observe(scanItemsEl.value)
    }

    watch(scanItemsEl, (el, oldEl) => {
        if (oldEl && resizeObserver) {
            resizeObserver.unobserve(oldEl) // stop observing previous element
        }
        if (el && resizeObserver) {
            resizeObserver.observe(el) // observe new element
        }
    }, { immediate: true })

    calculateHeight()
})
</script>

<style scoped>
.accordion-toggle {
    transform: rotate(180deg);
}

.scanner-icon {
    width: 28px;
}

.camera-icon {
    width: 32px;
}

ion-toggle::part(track) {
    background-color: var(--ion-color-coral);
}
</style>

<style>
.height-auto {
    --height: auto;
}
</style>
